import React, {
  type CSSProperties,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import useMouse, { type MousePosition } from "@react-hook/mouse-position";
import { type ElementHint, useDeviceContext } from "./DeviceContext";
import { Tooltip } from "@mui/material";
import { type DeviceContextState } from "../test-builder-page";
import {
  getHoveredElement,
  getMouseHint,
  toPercent,
} from "@/components/device-provider/utils";
import SelectCommandDialog from "@/components/device-provider/select-command-dialog";
import { filterNonEmptyStrings, normalizeText } from "@/utils/helpers";

type AnnotationState = "default" | "hidden" | "hovered" | "selected";

const Annotation = ({
  element,
  deviceWidth,
  deviceHeight,
  state,
  onClick,
}: {
  element: UIElement;
  deviceWidth: number;
  deviceHeight: number;
  state: AnnotationState;
  onClick: () => void;
}) => {
  if (element.bounds == null || state === "hidden") return null;
  const { x, y, width, height } = element.bounds;
  const l = `${(x / deviceWidth) * 100}%`;
  const t = `${(y / deviceHeight) * 100}%`;
  const w = `${(width / deviceWidth) * 100}%`;
  const h = `${(height / deviceHeight) * 100}%`;

  let className = "border border-dashed border-pink-400/60";
  let style: CSSProperties = {};

  if (state === "hovered") {
    className = "border-4 border-blue-500 active:active:bg-blue-400/40 z-10";
    style = {
      boxShadow: "0 0 0 9999px rgba(244, 114, 182, 0.2)",
    };
  } else if (state === "selected") {
    className = "border-4 border-blue-500 z-10";
    style = {
      boxShadow: "0 0 0 9999px rgba(96, 165, 250, 0.4)",
    };
  }

  return (
    <>
      <div
        className={`absolute ${className} shadow-pink-400`}
        style={{
          left: l,
          top: t,
          width: w,
          height: h,
          ...style,
        }}
        onClick={onClick}
      />
    </>
  );
};

const Crosshairs = ({
  cx,
  cy,
  color,
}: {
  cx: number;
  cy: number;
  color: string;
}) => {
  return (
    <>
      <div
        className={`absolute z-20 w-[1px] h-full ${color} -translate-x-1/2 pointer-events-none`}
        style={{
          top: 0,
          bottom: 0,
          left: `${cx * 100}%`,
        }}
      />
      <div
        className={`absolute z-20 h-[1px] w-full ${color} -translate-y-1/2 pointer-events-none`}
        style={{
          left: 0,
          right: 0,
          top: `${cy * 100}%`,
        }}
      />
    </>
  );
};

export const AnnotatedScreenshot = ({
  deviceContextState,
  usingGptDriverDeviceProvider,
  setDeviceContextState,
}: {
  deviceContextState?: DeviceContextState;
  usingGptDriverDeviceProvider: boolean;
  setDeviceContextState: Dispatch<SetStateAction<DeviceContextState>>;
}) => {
  const {
    deviceScreen,
    hoveredElement,
    inspectedElement,
    setInspectedElement,
    setFooterHint,
    setHoveredElement,
    footerHint,
  } = useDeviceContext();
  const ref = useRef(null);
  const [hasMouseLeft, setHasMouseLeft] = useState<boolean>(false);
  const mouse = useMouse(ref, { enterDelay: 100, leaveDelay: 100 });

  const [showSelectCommandDialog, setShowSelectCommandDialog] = useState(false);
  const [options, setOptions] = useState<string[]>([]);

  const handleAnnotationClick = useCallback(() => {
    if (!deviceContextState?.uiInspectorEnabled) {
      return;
    }

    const newOptions = filterNonEmptyStrings(hoveredElement ?? {});

    const getParsedOption = (key: string) => {
      const value = normalizeText(newOptions[key]);

      if (key === "resourceId") {
        return [
          `tapOn.id: "${value}"`,
          `assertVisible.id: "${value}"`,
          `assertNotVisible.id: "${value}"`,
          `scrollUpUntilVisible.id: "${value}"`,
          `scrollDownUntilVisible.id: "${value}"`,
          `multiTap.id: "${value}" count: 2`,
          `multiTap.id: "${value}" count: 3`,
        ];
      } else if (key === "text" || key === "accessibilityText") {
        return [
          `tapOn: "${value}"`,
          `assertVisible.text: "${value}"`,
          `assertNotVisible.text: "${value}"`,
          `scrollUpUntilVisible.text: "${value}"`,
          `scrollDownUntilVisible.text: "${value}"`,
          `multiTap.text: "${value}" count: 2`,
          `multiTap.text: "${value}" count: 3`,
        ];
      }
      return [];
    };

    const parsedOptions = [
      ...new Set(
        Object.keys(newOptions)
          .filter((option) => option !== "id")
          .map(getParsedOption)
          .flat()
          .sort((a, b) => {
            // Priority order
            const getPriority = (option: string) => {
              if (option.startsWith("tapOn:")) return 1;
              if (option.startsWith("tapOn.id:")) return 2;
              if (option.startsWith("multiTap")) return 3;
              if (option.startsWith("assertVisible")) return 4;
              if (option.startsWith("assertNotVisible")) return 5;
              if (option.startsWith("scrollUpUntilVisible")) return 6;
              if (option.startsWith("scrollDownUntilVisible")) return 7;
            };

            const priorityDiff = getPriority(a) - getPriority(b);
            if (priorityDiff !== 0) return priorityDiff;

            // If same priority, sort alphabetically
            return a.localeCompare(b);
          })
      ),
    ];

    setOptions(parsedOptions);
    setShowSelectCommandDialog(true);
  }, [hoveredElement, deviceContextState]);

  const getElementHint = useCallback(
    (element: UIElement): ElementHint => {
      if (deviceScreen == null) {
        return { text: `0%, 0%`, isResourceId: false };
      }
      if (element.text ?? element.accessibilityText) {
        return {
          text: normalizeText(element.text ?? element.accessibilityText ?? ""),
          isResourceId: false,
        };
      }
      if (element.resourceId) {
        return { text: normalizeText(element.resourceId), isResourceId: true };
      }
      if (element.bounds == null) {
        return { text: "", isResourceId: false };
      }
      const cx = toPercent(
        element.bounds.x + element.bounds.width / 2,
        deviceScreen.width
      );
      const cy = toPercent(
        element.bounds.y + element.bounds.height / 2,
        deviceScreen.height
      );
      return { text: `${cx}, ${cy}`, isResourceId: false };
    },
    [deviceScreen]
  );

  const onHover = useCallback(
    (element: UIElement | null, mouse: MousePosition | null) => {
      const mouseHint = mouse == null ? null : getMouseHint(mouse);
      const elementHint = element == null ? null : getElementHint(element);
      setFooterHint(
        elementHint ?? { text: mouseHint ?? "", isResourceId: false }
      );
      setHoveredElement(element?.id ? element : null);
    },
    [getElementHint, setFooterHint, setHoveredElement] // This is where you put dependencies for the onHover function
  );

  useEffect(() => {
    if (deviceScreen == null) return;
    if (mouse.isOver) {
      setHasMouseLeft(false);
      if (
        ((deviceContextState?.recordInteractionsEnabled ?? false) ||
          deviceContextState?.uiInspectorEnabled) ??
        false
      ) {
        const hoveredElement = getHoveredElement({
          dimensions: {
            width: deviceScreen.width,
            height: deviceScreen.height,
          },
          mouse,
          usingGptDriverDeviceProvider,
          elements: deviceScreen.elements,
        });
        onHover(hoveredElement, mouse);
      } else {
        onHover(null, mouse);
      }
    } else if (!hasMouseLeft) {
      setHasMouseLeft(true);
      onHover(null, null);
    }
  }, [
    deviceScreen,
    mouse,
    deviceContextState,
    hasMouseLeft,
    onHover,
    usingGptDriverDeviceProvider,
  ]);

  const focusedElement = inspectedElement != null || hoveredElement;

  const createCrosshairs = () => {
    if (!mouse.isOver) {
      const bounds = focusedElement?.bounds;
      if (bounds == null || deviceScreen == null) return null;
      const { x, y, width, height } = bounds;
      const cx = (x + width / 2) / deviceScreen.width;
      const cy = (y + height / 2) / deviceScreen.height;
      const color =
        focusedElement === inspectedElement ? "bg-pink-400" : "bg-blue-400";
      return <Crosshairs cx={cx} cy={cy} color={color} />;
    } else {
      if (mouse.x && mouse.y && mouse.elementWidth && mouse.elementHeight) {
        return (
          <Crosshairs
            cx={mouse.x / mouse.elementWidth}
            cy={mouse.y / mouse.elementHeight}
            color="bg-blue-400"
          />
        );
      } else {
        return null;
      }
    }
  };

  const uiInspectorEnabled =
    (deviceContextState?.recordInteractionsEnabled ||
      deviceContextState?.uiInspectorEnabled) ??
    false;

  return (
    <>
      <SelectCommandDialog
        open={showSelectCommandDialog}
        handleClose={() => {
          setShowSelectCommandDialog(false);
        }}
        options={options}
        setDeviceContextState={setDeviceContextState}
      />
      <Tooltip
        title={uiInspectorEnabled ? footerHint?.text : null}
        followCursor
      >
        <div
          ref={ref}
          className="relative overflow-hidden"
          style={{
            height: "100%",
            // aspectRatio:
            //   deviceScreen != null
            //     ? deviceScreen.width / deviceScreen.height
            //     : 9 / 20,
          }}
          onClick={() => {
            if (inspectedElement != null) {
              setInspectedElement(null);
            }
          }}
        >
          {uiInspectorEnabled && createCrosshairs()}
          {uiInspectorEnabled && (
            <div className="w-full h-full">
              {deviceScreen?.elements.map((element, index) => (
                <CreateAnnotation
                  key={index}
                  element={element}
                  inspectedElement={inspectedElement}
                  hoveredElement={hoveredElement}
                  deviceScreen={deviceScreen}
                  footerHint={footerHint}
                  handleClick={handleAnnotationClick}
                />
              ))}
            </div>
          )}
        </div>
      </Tooltip>
    </>
  );
};

interface CreateAnnotationProps {
  element: UIElement;
  inspectedElement: UIElement | null;
  hoveredElement: UIElement | null;
  deviceScreen: { width: number; height: number } | null;
  footerHint: { isResourceId: boolean; text: string } | null;
  handleClick: () => void;
}

const CreateAnnotation = ({
  element,
  inspectedElement,
  hoveredElement,
  deviceScreen,
  handleClick,
}: CreateAnnotationProps) => {
  let state: AnnotationState = "default";
  if (inspectedElement?.id === element?.id) {
    state = "selected";
  } else if (inspectedElement !== null) {
    state = "hidden";
  } else if (hoveredElement?.id === element?.id) {
    state = "hovered";
  }

  return (
    <Annotation
      key={element.id}
      element={element}
      deviceWidth={deviceScreen?.width || 0}
      deviceHeight={deviceScreen?.height || 0}
      state={state}
      onClick={handleClick}
    />
  );
};

export { Crosshairs, CreateAnnotation, Annotation };
